import { Component, OnInit, OnDestroy } from '@angular/core';
import { ParamContext } from '../../shared/service/param.context';
import { flatMap } from 'rxjs/operators';
import { CropGenotypingParameter } from '../../shared/crop/model/crop-genotyping-parameter';
import { GenotypingBrapiService } from '../../shared/brapi/service/genotyping-brapi.service';
import { GermplasmDetailsContext } from '../germplasm-details.context';
import { Study } from '../../shared/brapi/model/study/study';
import { VariantSet } from '../../shared/brapi/model/variantsets/variantset';
import { Call } from '../../shared/brapi/model/calls/call';
import { Germplasm } from '../../shared/brapi/model/germplasm/germplasm';
import { CallSet } from '../../shared/brapi/model/callsets/callset';
import { EMPTY } from 'rxjs';
import { AlertService } from '../../shared/alert/alert.service';
import { GermplasmService } from '../../shared/germplasm/service/germplasm.service';
import { Sample } from '../../entities/sample';
import { SearchGermplasmRequest } from '../../shared/brapi/model/germplasm/search-germplasm-request';
import { SearchSamplesRequest } from '../../shared/brapi/model/samples/search-samples-request';
import { HttpClient } from '@angular/common/http';
import { GenotypingParameterUtilService } from '../../shared/genotyping/genotyping-parameter-util.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FlapjackViewComponent } from './flapjack-view/flapjack-view.component';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';

@Component({
    selector: 'jhi-genotyping-pane',
    templateUrl: './genotyping-pane.component.html',
    styleUrls: ['./genotyping-pane.component.css'],
    providers: [{ provide: 'test', useValue: '' }],
})
export class GenotypingPaneComponent implements OnInit, OnDestroy {

    totalCount = 10;
    page = 1;
    pageSize = 10;
    isExportingFlapjack = false;
    isStudyLoading = false;
    isVariantSetLoading = false;
    isSamplesLoading = false;
    isGenotypingCallsLoading = false;

    public readonly LINK_BY_GUID = 'GUID';
    public readonly LINK_BY_SAMPLE_UID = 'SAMPLE_UID';
    public readonly LINK_BY_NAME = 'NAME';

    linkByOptions: string[] = [this.LINK_BY_GUID, this.LINK_BY_SAMPLE_UID, this.LINK_BY_NAME];

    selectedLinkBy: string = this.LINK_BY_GUID;
    germplasmSearchValue;
    selectedGenotypingStudy: Study;
    selectedVariantSet: VariantSet;
    genotypingStudies: Study[] = [];
    genotypingVariantsets: VariantSet[] = [];
    genotypingGermplasm: Germplasm;
    genotypingCallSets: CallSet[];
    genotypingCalls: Call[];
    germplasmSamples: Sample[];

    cropGenotypingParameter: CropGenotypingParameter;

    subscriptionsManager = new SubscriptionsManager();

    constructor(
        private context: ParamContext,
        private genotypingBrapiService: GenotypingBrapiService,
        private genotypingParameterUtilService: GenotypingParameterUtilService,
        public germplasmDetailsContext: GermplasmDetailsContext,
        public germplasmService: GermplasmService,
        public alertService: AlertService,
        private modalService: NgbModal,
        public http: HttpClient) {
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.subscriptionsManager.add = this.genotypingParameterUtilService.getGenotypingParametersAndAuthenticate().subscribe(
            (cropGenotypingParameter) => {
                this.cropGenotypingParameter = cropGenotypingParameter;
                this.genotypingBrapiService.brapiEndpoint = this.removeTrailingSlash(cropGenotypingParameter.endpoint);
                this.genotypingBrapiService.baseUrl = this.removeTrailingSlash(cropGenotypingParameter.baseUrl);
                this.genotypingBrapiService.accessToken = this.removeTrailingSlash(cropGenotypingParameter.accessToken);
                this.linkBySelectOnChange();
            }, (error) => {
                this.alertService.error(error);
            });
    }

    linkBySelectOnChange() {
        this.resetForm();
        if (this.selectedLinkBy === this.LINK_BY_SAMPLE_UID) {
            this.isSamplesLoading = true;
            this.subscriptionsManager.add = this.germplasmService.getGermplasmSamplesByGid(this.germplasmDetailsContext.gid).subscribe((samples) => {
                this.germplasmSamples = samples;
                this.isSamplesLoading = false;
            });
        } else if (this.selectedLinkBy === this.LINK_BY_GUID) {
            this.germplasmSearchValue = this.germplasmDetailsContext.germplasm.germplasmUUID;
            this.linkByChanged();
        }
    }

    linkByChanged() {
        this.resetForm();
        this.isStudyLoading = true;
        let searchGermplasmRequest: SearchGermplasmRequest;
        if (this.selectedLinkBy === this.LINK_BY_NAME) {
            searchGermplasmRequest = {
                germplasmNames: [this.germplasmSearchValue],
                programDbIds: [this.cropGenotypingParameter.programId]
            };
            this.loadGenotypingStudy(searchGermplasmRequest);
        } else if (this.selectedLinkBy === this.LINK_BY_GUID) {
            searchGermplasmRequest = {
                externalReferenceIds: [this.germplasmSearchValue],
                programDbIds: [this.cropGenotypingParameter.programId]
            };
            this.loadGenotypingStudy(searchGermplasmRequest);
        } else if (this.selectedLinkBy === this.LINK_BY_SAMPLE_UID) {
            const searchSamplesRequest: SearchSamplesRequest = {
                externalReferenceIds: [this.germplasmSearchValue],
                programDbIds: [this.cropGenotypingParameter.programId]
            };
            this.searchSamples(searchSamplesRequest);
        }
    }

    searchSamples(searchSamplesRequest: SearchSamplesRequest) {
        // Search the Sample first by externalReferenceID,
        // If sample is available, we should get the germplasm associated to it, and use the germplasmDbId to
        // load the Gigwa study and calls.
        this.subscriptionsManager.add = this.genotypingBrapiService.searchSamples(searchSamplesRequest).subscribe((response) => {
            if (response && response.result.data.length) {
                this.loadGenotypingStudy({
                    germplasmDbIds: [response.result.data[0].germplasmDbId],
                    programDbIds: [this.cropGenotypingParameter.programId]
                });
            } else {
                this.isStudyLoading = false;
                this.alertService.error('genotyping.no.genotyping.germplasm.found', {
                    param2: this.selectedLinkBy,
                    param1: this.germplasmSearchValue
                });
            }
        });
    }

    loadGenotypingStudy(searchGermplasmRequest: SearchGermplasmRequest) {
        this.subscriptionsManager.add = this.genotypingBrapiService.searchGermplasm(searchGermplasmRequest).pipe(flatMap((response) => {
            if (response && response.result.data.length) {
                this.genotypingGermplasm = response.result.data[0];
                return this.genotypingBrapiService.searchStudies({
                    germplasmDbIds: [this.genotypingGermplasm.germplasmDbId],
                    programDbIds: [this.cropGenotypingParameter.programId]
                });
            } else {
                this.isStudyLoading = false;
                this.alertService.error('genotyping.no.genotyping.germplasm.found', {
                    param2: this.selectedLinkBy,
                    param1: this.germplasmSearchValue
                });
            }
            return EMPTY;
        })).subscribe((brapiResponse) => {
            if (brapiResponse && brapiResponse.result.data.length) {
                this.genotypingStudies = brapiResponse.result.data;
            } else {
                this.alertService.error('genotyping.no.genotyping.studies.found');
            }
            this.isStudyLoading = false;
        }, (error) => {
            // FIXME: Gigwa server throws an http 500 error if the germplasm is not found.
            this.alertService.error('genotyping.no.genotyping.germplasm.found', {
                param2: this.selectedLinkBy,
                param1: this.germplasmSearchValue
            });
            this.isStudyLoading = false;
        });
    }

    selectStudyOnChange() {
        if (this.selectedGenotypingStudy) {
            this.isVariantSetLoading = true;
            this.subscriptionsManager.add = this.genotypingBrapiService.searchVariantsets({ studyDbIds: [this.selectedGenotypingStudy.studyDbId] }).subscribe((brapiResponse) => {
                if (brapiResponse && brapiResponse.result.data.length) {
                    this.genotypingVariantsets = brapiResponse.result.data;
                } else {
                    this.alertService.error('genotyping.no.genotyping.variantsets.found');
                }
                this.isVariantSetLoading = false;
            });
        }
    }

    selectVariantsetOnChange() {
        this.loadGenotypesTable();
    }

    loadGenotypesTable() {
        if (this.selectedVariantSet) {
            this.subscriptionsManager.add = this.genotypingBrapiService.searchCallsets({
                variantSetDbIds: [this.selectedVariantSet.variantSetDbId],
                germplasmDbIds: [this.genotypingGermplasm.germplasmDbId]
            }).subscribe((brapiResponse) => {
                if (brapiResponse && brapiResponse.result.data.length) {
                    this.genotypingCallSets = brapiResponse.result.data;
                    this.loadGenotypingCalls();
                } else {
                    this.alertService.error('genotyping.no.genotyping.callsets.found');
                }
            });
        }
    }

    loadGenotypingCalls() {
        if (this.genotypingCallSets && this.genotypingCallSets.length > 0) {
            const callSetDbIds = this.genotypingCallSets.map((callSet) => callSet.callSetDbId);
            this.isGenotypingCallsLoading = true;
            this.subscriptionsManager.add = this.genotypingBrapiService.searchCalls({
                expandHomozygotes: true,
                callSetDbIds: callSetDbIds,
                pageSize: this.pageSize,
                page: (this.page - 1)
            }).subscribe(((brapiResponse) => {
                this.genotypingCalls = brapiResponse.result.data;
                this.totalCount = brapiResponse.metadata.pagination.totalCount;
                this.isGenotypingCallsLoading = false;
            }));
        }
    }

    getFileUrl(url): Promise<string> {
        return new Promise<string>((resolve) => {
            this.subscriptionsManager.add = this.http.head(url, { observe: 'response' }).subscribe(
                (response) => {
                    // If file doesn’t exist on the server, return undefined.
                    if (response.status === 200) {
                        resolve(url);
                    } else {
                        resolve(undefined);
                    }
                },
                () => {
                    resolve(undefined);
                }
            );
        });
    }

    showFlapjackModal() {
        const modalRef = this.modalService.open(FlapjackViewComponent, { size: 'autofit', backdrop: 'static' });
        modalRef.componentInstance.selectedVariantSet = this.selectedVariantSet;
        modalRef.componentInstance.cropGenotypingParameter = this.cropGenotypingParameter;
        modalRef.componentInstance.genotypingGermplasm = this.genotypingGermplasm;
    }


    resetForm() {
        this.genotypingGermplasm = null;
        this.genotypingStudies = [];
        this.genotypingVariantsets = [];
        this.selectedGenotypingStudy = null;
        this.selectedVariantSet = null;
        this.genotypingCallSets = [];
        this.genotypingCalls = null;
    }

    getCallsetAndVariantCountText() {
        return `${this.selectedVariantSet.callSetCount} individuals; ${this.selectedVariantSet.variantCount} markers`;
    }

    removeTrailingSlash(url: string): string {
        return url.replace(/\/+$/, '');
    }

}
